import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom'
import {
  ArrowLeft, ArrowRight, Send, Upload, CheckCircle2, Check, AlertCircle, FileText,
  MapPin, Info, Plus, X, User,
} from 'lucide-react'
import {
  getJobDetail, applyJob, uploadApplicationDocument, getMyApplications, getEnums, WORK_TYPE_LABELS, EXPERIENCE_RANGE_LABELS,
  type ApiJobDetail, type ApplicationDocumentKind, type ApplyJobPayload, type ScreeningQuestion,
} from '@/services/jobService'
import { getProfile, updateProfile, type IndividualProfile } from '@/services/profileService'
import { useAuthStore } from '@/store/authStore'
import { needsPreAssessment, preAssessmentPath } from '@/pages/Assessment/preAssessmentStorage'
import { useProvinces, normalizeProvince } from '@/services/addressService'
import SearchableSelect from '@/components/ui/SearchableSelect'
import applyBg from './assets/apply-bg.png'
import CompanyLogo from '@/components/ui/CompanyLogo'
import { filterPhone, isValidPhone } from '@/lib/inputFilters'

const ACCEPTED = ['.doc', '.docx', '.pdf', '.txt']
const MAX_MB = 10   // ต้องตรงกับ RULES["APPLICATION_DOC"] ใน API (อัปโหลดตรง)
const MAX_SKILLS = 5

// ── Step definitions ──────────────────────────────────────────────────────────
// "ข้อมูลส่วนตัว" เป็น step แบบมีเงื่อนไข — ข้ามไปเลยถ้าโปรไฟล์มี ชื่อ/นามสกุล/เบอร์/จังหวัด ครบแล้ว
// (4 ช่องนี้เท่านั้นที่ผูกกับโปรไฟล์ m_individual — ข้อมูลขั้นอื่นเป็นของใบสมัครแต่ละใบ t_job_application)
// หมายเหตุดีไซน์: รอบนี้รีดีไซน์แค่ header/step-indicator/Step 1 เท่านั้น (ตามที่ขอ) — เนื้อหา step 2-5
// (documents/questions/history/review) ยังใช้ของเดิมไว้ก่อน ยังไม่ได้ปรับดีไซน์ใหม่ รอ spec เพิ่มเติม
type StepId = 'profile' | 'documents' | 'questions' | 'history' | 'review'
const ALL_STEPS: { id: StepId; label: string; sub: string }[] = [
  { id: 'profile', label: 'ข้อมูลส่วนตัว', sub: 'กรอกข้อมูลพื้นฐาน' },
  { id: 'documents', label: 'อัปโหลดข้อมูล', sub: 'เรซูเม่ และจดหมายสมัครงาน' },
  { id: 'questions', label: 'ตอบคำถาม', sub: 'ข้อมูลเพิ่มเติม' },
  { id: 'history', label: 'ประวัติและทักษะ', sub: 'ประวัติการทำงาน การศึกษา และทักษะ' },
  { id: 'review', label: 'ตรวจสอบและส่ง', sub: 'ตรวจสอบข้อมูลทั้งหมด' },
]

interface WorkHistoryItem {
  position: string
  company: string
  startDate: string
  endDate: string
  current: boolean   // ยังทำงานอยู่ตำแหน่งนี้ — ถ้าติ๊กไว้ไม่ต้องกรอกวันที่สิ้นสุด
  description: string
}

interface EducationItem {
  university: string
  faculty: string   // คณะ
  major: string      // สาขา
  gpa: string
  graduated: boolean // จบการศึกษาแล้วหรือยัง — ผลต่อความหมายของ graduationDate ด้านล่าง
  graduationDate: string // graduated=true → วันที่จบจริง, graduated=false → วันที่คาดว่าจะจบ
}

interface WizardState {
  firstName: string
  lastName: string
  province: string
  phone: string
  // ไฟล์แนบ — ยื่นใบสมัครสำเร็จแล้วค่อยอัปโหลดขึ้น MinIO ทีละไฟล์ (uploadApplicationDocument)
  resumeChoice: 'none' | 'upload'
  resumeFile: File | null
  coverLetterMode: 'upload' | 'write' | 'none'
  coverLetterFile: File | null
  coverLetterText: string
  experience: string        // รหัส enum ExperienceRange (NONE, LT_1, Y1 ...) — '' = ไม่ระบุ
  expectedSalary: string
  screeningAnswers: Record<string, boolean | string>   // {question_id: คำตอบ}
  workHistory: WorkHistoryItem[]
  education: EducationItem[]
  educationFile: File | null
  certificateFiles: File[]
  skills: string[]
}

// คำถามเพิ่มเติมของบริษัท 1 ข้อ — แสดงตามชนิดคำถาม (ScreeningQuestionType)
function ScreeningQuestionField({ question, value, error, onChange }: {
  question: ScreeningQuestion
  value: boolean | string | undefined
  error?: string
  onChange: (value: boolean | string) => void
}) {
  const choiceCls = (active: boolean) =>
    `border px-4 py-2 text-sm font-medium transition-colors ${
      active ? 'border-accent bg-accent/5 text-accent' : 'border-[#E7E7F0] text-text-muted hover:text-text'
    }`
  return (
    <div>
      <p className="mb-2 text-[13px] font-medium text-text">
        {question.question} <span className="text-rose-500">*</span>
      </p>
      {question.type === 'YES_NO' && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onChange(true)} className={choiceCls(value === true)}>ใช่</button>
          <button type="button" onClick={() => onChange(false)} className={choiceCls(value === false)}>ไม่ใช่</button>
        </div>
      )}
      {question.type === 'CHOICE' && (
        <div className="flex flex-wrap gap-2">
          {(question.options ?? []).map(opt => (
            <button key={opt} type="button" onClick={() => onChange(opt)} className={choiceCls(value === opt)}>{opt}</button>
          ))}
        </div>
      )}
      {question.type === 'NUMBER' && (
        <input value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)}
          className={`${inputCls} sm:max-w-xs`} inputMode="decimal" placeholder="ระบุเป็นตัวเลข" />
      )}
      {question.type === 'TEXT' && (
        <AutoGrowTextarea
          value={typeof value === 'string' ? value : ''}
          minHeight={80}
          onChange={e => onChange(e.target.value)}
          className="border border-[#E7E7F0] bg-white px-3.5 py-2.5 text-sm text-text placeholder-text-muted/60 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/25 leading-relaxed"
        />
      )}
      {error && <p className="mt-1.5 text-xs text-rose-500">{error}</p>}
    </div>
  )
}

const EMPTY_WORK_ITEM: WorkHistoryItem = { position: '', company: '', startDate: '', endDate: '', current: false, description: '' }
const EMPTY_EDUCATION_ITEM: EducationItem = { university: '', faculty: '', major: '', gpa: '', graduated: false, graduationDate: '' }

// ภาพประกอบเส้น outline ง่ายๆ สีม่วง — เอกสาร/โปรไฟล์ ใช้กับการ์ดสนับสนุนใน step "ข้อมูลส่วนตัว"
function ApplicationIllustration() {
  return (
    <svg width="128" height="112" viewBox="0 0 128 112" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="16" y="14" width="70" height="90" rx="8" fill="#F5F3FF" stroke="#6C63FF" strokeWidth="1.5" />
      <rect x="34" y="32" width="70" height="90" rx="8" fill="white" stroke="#6C63FF" strokeWidth="1.5" />
      <circle cx="69" cy="58" r="12" fill="#F5F3FF" stroke="#6C63FF" strokeWidth="1.5" />
      <path d="M64 58a5 5 0 1 1 10 0 5 5 0 0 1-10 0Zm5 7c-4 0-7 2-7 4.5V71h14v-1.5c0-2.5-3-4.5-7-4.5Z" fill="#6C63FF" />
      <line x1="46" y1="82" x2="92" y2="82" stroke="#E7E5FF" strokeWidth="2" strokeLinecap="round" />
      <line x1="46" y1="92" x2="80" y2="92" stroke="#E7E5FF" strokeWidth="2" strokeLinecap="round" />
      <line x1="46" y1="102" x2="86" y2="102" stroke="#E7E5FF" strokeWidth="2" strokeLinecap="round" />
      <circle cx="108" cy="48" r="10" fill="#6C63FF" />
      <path d="M108 43v6M108 52.5h.01" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M20 20h6M20 26h4" stroke="#6C63FF" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function SuccessScreen({ job, onBack, uploadWarning }: { job: ApiJobDetail; onBack: () => void; uploadWarning: string }) {
  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[#F8F8FC] pb-16 pt-24">
      {/* พื้นหลัง logistics แบบจางๆ — ภาพเดียวกับหน้า In-house/จองอบรม ให้หน้าสำเร็จทุกหน้าธีมเดียวกัน */}
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <div className="relative mx-auto max-w-md px-4 text-center">
        <div className="mb-6 flex h-16 w-16 mx-auto items-center justify-center rounded-full border-2 border-accent/25 bg-white">
          <CheckCircle2 className="h-8 w-8 text-accent" />
        </div>
        <h1 className="text-2xl font-bold text-text mb-2">ส่งใบสมัครเรียบร้อยแล้ว</h1>
        <p className="text-sm text-text-muted mb-0.5">ใบสมัครของคุณถูกส่งไปยัง {job.company.company_name} เรียบร้อยแล้ว</p>
        <p className="text-sm font-medium text-text mb-8">{job.title}</p>
        {uploadWarning && (
          <p className="mb-5 border border-amber-200 bg-amber-50 p-3 text-left text-xs text-amber-700">{uploadWarning}</p>
        )}

        <div className="mb-5 border border-[#E7E7F0] bg-white p-5 text-left">
          <div className="flex items-center gap-3">
            <CompanyLogo name={job.company.company_name} logoUrl={job.company.logo_url} size={44} className="rounded-none" />
            <div>
              <p className="text-sm font-semibold text-text">{job.company.company_name}</p>
              <p className="text-xs text-text-muted">{job.title}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[#E7E7F0] pt-4 text-xs text-text-muted">
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{job.location}</span>
            <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{WORK_TYPE_LABELS[job.work_type] ?? job.work_type}</span>
          </div>
        </div>

        <p className="mb-8 text-xs leading-relaxed text-text-muted">
          หากบริษัทมีการอัปเดตสถานะใบสมัครหรือนัดสัมภาษณ์ คุณสามารถติดตามได้ที่ "ใบสมัครของฉัน"
        </p>

        <div className="flex flex-col gap-3">
          <Link to="/account?tab=my-applications"
            className="bg-accent py-3 text-sm font-semibold text-white transition-colors hover:bg-accent2 text-center">
            ติดตามสถานะใบสมัคร
          </Link>
          <button onClick={onBack}
            className="border border-[#E7E7F0] bg-white py-3 text-sm text-text-muted hover:bg-[#F8F8FC] transition-colors">
            ดูตำแหน่งงานอื่น
          </button>
        </div>
      </div>
    </div>
  )
}

// Auto-growing textarea — no internal scrollbar, expands to fit content
// ความยาวสูงสุดของจดหมายแนะนำตัว (จำกัดที่หน้าเว็บ — backend จำกัดไว้กว้างกว่านี้ที่ 10,000 ตัวอักษร)
const COVER_LETTER_MAX = 1000

function AutoGrowTextarea({ value, minHeight = 140, className = '', ...props }: {
  value: string; minHeight?: number
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null)

  const resize = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`
  }, [minHeight])

  useEffect(() => { resize() }, [value, resize])

  return (
    <textarea
      ref={ref}
      value={value}
      onInput={resize}
      style={{ minHeight, overflow: 'hidden' }}
      className={`w-full resize-none ${className}`}
      {...props}
    />
  )
}

// ── Minimal circular stepper — วงกลมล้วน เชื่อมด้วยเส้นบางเส้นเดียวพาดกลางวงกลมทุกจุด
// เสร็จแล้ว = วงกลมม่วงทึบ + เครื่องหมายถูกสีขาว, step ปัจจุบัน = วงกลมม่วงใหญ่กว่าเล็กน้อย + เลข step
// + ring เรืองแสงม่วงจางๆ รอบวง, ยังไม่ถึง = วงกลมเทาอ่อน/ม่วงจาง + เลข step สีเทาเข้ม ──
// แจ้งผู้สมัครก่อนส่งใบสมัครว่าบริษัทเจ้าของประกาศจะเห็นข้อมูลอะไรบ้าง (PDPA) — ตรงกับสิ่งที่หน้าผู้สมัครฝั่งบริษัทแสดงจริง
function DisclosureNotice({ companyName }: { companyName: string }) {
  const rows: { label: string; detail: string }[] = [
    { label: 'ข้อมูลส่วนตัว', detail: 'ชื่อ-นามสกุล อีเมล เบอร์โทรศัพท์ และจังหวัด' },
    { label: 'ข้อมูลในใบสมัคร', detail: 'จดหมายสมัครงาน ประสบการณ์ เงินเดือนที่คาดหวัง คำตอบคำถามจากบริษัท ประวัติการทำงาน การศึกษา และทักษะ' },
    { label: 'ผลการประเมิน', detail: 'คะแนนแบบประเมินก่อนสมัครงาน (ถ้ามี)' },
    { label: 'ประวัติการเรียนรู้ใน JOS CoWork', detail: 'สกิลที่ได้รับ คอร์สที่เรียนจบ และการอบรมที่เข้าร่วม' },
  ]
  return (
    <div className="flex items-start gap-3 border border-[#D9D5FF] bg-[#FBFAFF] p-4 text-sm">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
      <div className="min-w-0">
        <p className="font-semibold text-text">ข้อมูลที่ {companyName} จะเห็นเมื่อคุณส่งใบสมัครนี้</p>
        <dl className="mt-2 space-y-1.5">
          {rows.map(r => (
            <div key={r.label} className="grid grid-cols-1 gap-x-4 sm:grid-cols-[210px_1fr]">
              <dt className="font-medium text-text">{r.label}</dt>
              <dd className="text-text-muted">{r.detail}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 leading-relaxed text-text-muted">
          บริษัทใช้ข้อมูลเหล่านี้เพื่อพิจารณารับเข้าทำงานเท่านั้น และจะไม่แสดงต่อบริษัทอื่น
          รายละเอียดการเก็บและใช้ข้อมูลอ่านได้ที่{' '}
          <Link to="/privacy" target="_blank" className="font-medium text-accent hover:underline">ประกาศความเป็นส่วนตัว</Link>
        </p>
      </div>
    </div>
  )
}

function StepIndicator({ steps, currentIndex }: { steps: { id: StepId; label: string; sub: string }[]; currentIndex: number }) {
  const progressPct = steps.length > 1 ? (currentIndex / (steps.length - 1)) * 100 : 0
  return (
    <div className="relative flex items-start">
      {/* เส้นเชื่อมเดียวพาดผ่านกลางวงกลมทั้งหมด — ส่วนที่ผ่านแล้วเป็นม่วง ที่เหลือเป็นเทาอ่อน */}
      <div className="absolute left-0 right-0 top-4 h-px bg-[#EDEBFA]" style={{ marginInline: `${100 / steps.length / 2}%` }}>
        <div className="h-px bg-accent/50 transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      {steps.map((step, i) => {
        const done = i < currentIndex
        const current = i === currentIndex
        const status = current ? 'กำลังดำเนินการ' : done ? 'เสร็จสิ้น' : 'รอดำเนินการ'
        return (
          <div key={step.id} className="relative flex flex-1 flex-col items-center text-center">
            <span
              className={
                current
                  ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white ring-4 ring-accent/10'
                  : done
                    ? 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-white'
                    : 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F1F0FF] text-xs font-semibold text-text-muted'
              }
            >
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <p className={`mt-2.5 px-1 text-[13px] font-semibold leading-snug ${
              current ? 'text-accent' : done ? 'text-text' : 'text-text-muted'
            }`}>
              {step.label}
            </p>
            <p className={`mt-0.5 text-[11px] leading-snug ${current ? 'text-accent' : 'text-text-muted'}`}>
              {status}
            </p>
          </div>
        )
      })}
    </div>
  )
}

function SectionTitle({ title, required, hint }: { title: string; required?: boolean; hint?: string }) {
  return (
    <div className="mb-4 flex gap-2.5 border-b border-[#EFEFF5] pb-3">
      <span className="mt-0.5 h-4 w-1 shrink-0 bg-accent" />
      <div>
        <h2 className="text-[13px] font-bold uppercase tracking-wide text-text">
          {title} {required && <span className="ml-0.5 text-rose-500">*</span>}
        </h2>
        {hint && <p className="mt-1 text-xs text-text-muted">{hint}</p>}
      </div>
    </div>
  )
}

const inputCls = 'h-11 w-full border border-[#E7E7F0] bg-white px-3.5 text-sm text-text placeholder-text-muted/60 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/25'

export default function JobApplicationPage() {
  const { data: provinces } = useProvinces()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const customerType = useAuthStore((s) => s.customerType)
  // ── Gate ด่านที่ 2: ต้องทำ "แบบประเมินก่อนสมัครงาน" (แบบประเมินกลาง ไม่ผูกกับตำแหน่งงาน) ให้เสร็จก่อน
  // ด่านแรกอยู่ที่ปุ่ม "สมัครงานตำแหน่งนี้" ในหน้า /jobs — ด่านนี้กันคนที่เข้า URL /jobs/:id/apply ตรงๆ
  // ถาม server ตอน mount — ถ้าต้องสอบพาไปหน้าแบบประเมิน (ด่านสุดท้ายคือ backend ตอบ 403 ตอนยื่นใบสมัคร)
  const [mustAssess, setMustAssess] = useState(false)
  useEffect(() => {
    needsPreAssessment(customerType).then(setMustAssess)
  }, [customerType])

  const [job, setJob] = useState<ApiJobDetail | null>(null)
  const [profile, setProfile] = useState<IndividualProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [alreadyApplied, setAlreadyApplied] = useState(false)
  const [isFirstApplication, setIsFirstApplication] = useState(false)   // ยังไม่เคยมีใบสมัครเลย → แสดงกล่องแจ้งข้อมูลที่บริษัทจะเห็น

  const [steps, setSteps] = useState(ALL_STEPS)
  const [stepIndex, setStepIndex] = useState(0)
  const [wizard, setWizard] = useState<WizardState>({
    firstName: '', lastName: '', province: '', phone: '',
    resumeChoice: 'none', resumeFile: null,
    coverLetterMode: 'write', coverLetterFile: null, coverLetterText: '',
    experience: '', expectedSalary: '', screeningAnswers: {},
    workHistory: [{ ...EMPTY_WORK_ITEM }],
    education: [{ ...EMPTY_EDUCATION_ITEM }],
    educationFile: null, certificateFiles: [], skills: [],
  })
  const [skillInput, setSkillInput] = useState('')
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({})
  // ลำดับตัวเลือกประสบการณ์มาจาก enum ExperienceRange (/api/enums) — โหลดไม่ได้ใช้ลำดับของป้ายภาษาไทยแทน
  const [experienceOptions, setExperienceOptions] = useState<string[]>(Object.keys(EXPERIENCE_RANGE_LABELS))

  const [screen, setScreen] = useState<'form' | 'success'>('form')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [uploadWarning, setUploadWarning] = useState('')

  const resumeInputRef = useRef<HTMLInputElement>(null)
  const coverLetterInputRef = useRef<HTMLInputElement>(null)
  const educationInputRef = useRef<HTMLInputElement>(null)
  const certificateInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getEnums()
      .then(d => { if (d.ExperienceRange?.length) setExperienceOptions(d.ExperienceRange) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!id || mustAssess) return
    let cancelled = false
    // loading เริ่มเป็น true อยู่แล้ว — ไม่ต้อง setLoading(true) ตรง ๆ ในตัว effect (react-hooks/set-state-in-effect)
    Promise.all([getJobDetail(id), getProfile(), getMyApplications().catch(() => [])])
      .then(([jobData, profileData, myApps]) => {
        if (cancelled) return
        setJob(jobData)
        // สมัครงานนี้ไปแล้ว → สมัครซ้ำไม่ได้ (กันคนเข้า URL /jobs/:id/apply ตรง ๆ — backend ก็ปฏิเสธอีกชั้น)
        if (myApps.some(a => a.job_id === jobData.job_id)) setAlreadyApplied(true)
        // สมัครงานครั้งแรก (ยังไม่เคยมีใบสมัครเลย) → แจ้งว่าจะเปิดเผยข้อมูลอะไรให้บริษัทบ้าง (ไม่เก็บค่าอะไรเพิ่ม — สมัครแล้วก็ไม่แสดงอีก)
        else if (myApps.length === 0) setIsFirstApplication(true)
        // getProfile() คืน IndividualProfile หรือ CompanyProfile ตาม customer_type — หน้านี้ต้อง INDIVIDUAL เท่านั้น
        if ('first_name' in profileData) {
          setProfile(profileData)
          const hasProfileInfo = Boolean(
            profileData.first_name && profileData.last_name && profileData.phone && profileData.province
          )
          Promise.resolve().then(() => {
            // ดึงจากโปรไฟล์แค่ 4 ช่องนี้ — ข้อมูลขั้นอื่น (Resume/ประสบการณ์/ประวัติ ฯลฯ) กรอกใหม่ทุกใบสมัคร
            setWizard(p => ({
              ...p,
              firstName: profileData.first_name,
              lastName: profileData.last_name,
              phone: profileData.phone || '',
              province: profileData.province || '',
            }))
            // เคยกรอกชื่อ-นามสกุล-เบอร์โทร-จังหวัดในโปรไฟล์ครบแล้ว → ข้ามขั้นตอน "ข้อมูลส่วนตัว" ไปเลย
            if (hasProfileInfo) setSteps(ALL_STEPS.filter(s => s.id !== 'profile'))
          })
        }
      })
      .catch(err => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'ไม่พบประกาศงานนี้') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, mustAssess])

  if (mustAssess && id) {
    return <Navigate to={preAssessmentPath(`/jobs/${id}/apply`)} replace />
  }

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8F8FC] pt-24 text-sm text-text-muted">กำลังโหลด...</div>
  }
  if (loadError || !job || !id) {
    navigate('/jobs', { replace: true })
    return null
  }

  if (alreadyApplied && screen !== 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F8FC] pt-24 pb-16">
        <div className="mx-auto max-w-md px-4 text-center">
          <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-accent" />
          <h1 className="mb-2 text-xl font-bold text-text">คุณสมัครงานนี้ไปแล้ว</h1>
          <p className="mb-6 text-sm text-text-muted">{job.title} · {job.company.company_name} — สมัครซ้ำไม่ได้</p>
          <button onClick={() => navigate('/jobs')}
            className="bg-accent px-6 py-3 text-sm font-semibold text-white hover:bg-accent2">ดูตำแหน่งงานอื่น</button>
        </div>
      </div>
    )
  }

  if (screen === 'success') {
    return <SuccessScreen job={job} onBack={() => navigate('/jobs')} uploadWarning={uploadWarning} />
  }

  const currentStep = steps[stepIndex].id
  const isFirstStep = stepIndex === 0
  const isLastStep = stepIndex === steps.length - 1
  const step1Valid = Boolean(wizard.firstName.trim() && wizard.lastName.trim() && wizard.province.trim() && isValidPhone(wizard.phone.trim()))
  const screeningQuestions = (job.screening_questions ?? []).filter(q => q.id)
  // แถวที่กรอกจริง (ใช้แสดงในขั้นตรวจสอบ — backend ก็ข้ามแถวว่างเหมือนกัน)
  const filledWork = wizard.workHistory.filter(w => w.position.trim() || w.company.trim() || w.description.trim())
  const filledEducation = wizard.education.filter(ed => ed.university.trim() || ed.faculty.trim() || ed.major.trim())

  const set = <K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    setWizard(p => ({ ...p, [key]: value }))
    setStepErrors(p => ({ ...p, [key]: '' }))
  }

  const handleResumeFile = (f: File | null) => {
    if (!f) return
    const ext = '.' + f.name.split('.').pop()?.toLowerCase()
    if (!ACCEPTED.includes(ext)) { setStepErrors(p => ({ ...p, resume: `ไม่รองรับไฟล์นามสกุล ${ext} — ใช้ .pdf, .doc, .docx หรือ .txt` })); return }
    if (f.size > MAX_MB * 1024 * 1024) { setStepErrors(p => ({ ...p, resume: `ไฟล์มีขนาดเกินกำหนด (สูงสุด ${MAX_MB} MB)` })); return }
    // เก็บแค่ File ไว้แสดงชื่อ — เลิกสร้างลิงก์ blob: (เคยถูกส่งเป็น resume_url ที่ใช้ไม่ได้)
    setWizard(p => ({ ...p, resumeFile: f }))
    setStepErrors(p => ({ ...p, resume: '' }))
  }

  const handleCoverLetterFile = (f: File | null) => {
    if (!f) return
    const ext = '.' + f.name.split('.').pop()?.toLowerCase()
    if (!ACCEPTED.includes(ext)) { setStepErrors(p => ({ ...p, coverLetter: `ไม่รองรับไฟล์นามสกุล ${ext}` })); return }
    if (f.size > MAX_MB * 1024 * 1024) { setStepErrors(p => ({ ...p, coverLetter: `ไฟล์มีขนาดเกินกำหนด (สูงสุด ${MAX_MB} MB)` })); return }
    setWizard(p => ({ ...p, coverLetterFile: f }))
    setStepErrors(p => ({ ...p, coverLetter: '' }))
  }

  const setScreeningAnswer = (questionId: string, value: boolean | string) => {
    setWizard(p => ({ ...p, screeningAnswers: { ...p.screeningAnswers, [questionId]: value } }))
    setStepErrors(p => ({ ...p, [`sq_${questionId}`]: '' }))
  }

  const addSkill = () => {
    const v = skillInput.trim()
    if (!v) return
    if (wizard.skills.length >= MAX_SKILLS) { setStepErrors(p => ({ ...p, skills: `เพิ่มได้สูงสุด ${MAX_SKILLS} ข้อ` })); return }
    if (wizard.skills.includes(v)) { setSkillInput(''); return }
    set('skills', [...wizard.skills, v])
    setSkillInput('')
  }
  const removeSkill = (i: number) => set('skills', wizard.skills.filter((_, idx) => idx !== i))

  const updateWorkItem = <K extends keyof WorkHistoryItem>(i: number, field: K, value: WorkHistoryItem[K]) => {
    const next = [...wizard.workHistory]
    next[i] = { ...next[i], [field]: value }
    set('workHistory', next)
  }
  const addWorkItem = () => set('workHistory', [...wizard.workHistory, { ...EMPTY_WORK_ITEM }])
  const removeWorkItem = (i: number) => set('workHistory', wizard.workHistory.filter((_, idx) => idx !== i))

  const updateEducationItem = <K extends keyof EducationItem>(i: number, field: K, value: EducationItem[K]) => {
    const next = [...wizard.education]
    next[i] = { ...next[i], [field]: value }
    set('education', next)
  }
  const addEducationItem = () => set('education', [...wizard.education, { ...EMPTY_EDUCATION_ITEM }])
  const removeEducationItem = (i: number) => set('education', wizard.education.filter((_, idx) => idx !== i))

  const validateStep = (): boolean => {
    const e: Record<string, string> = {}
    if (currentStep === 'profile') {
      if (!wizard.firstName.trim()) e.firstName = 'กรุณากรอกชื่อ'
      if (!wizard.lastName.trim()) e.lastName = 'กรุณากรอกนามสกุล'
      if (!wizard.province.trim()) e.province = 'กรุณากรอกจังหวัด'
      if (!wizard.phone.trim()) e.phone = 'กรุณากรอกเบอร์โทรศัพท์'
      else if (!isValidPhone(wizard.phone.trim())) e.phone = 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 9–10 หลัก ขึ้นต้นด้วย 0'
    }
    if (currentStep === 'documents') {
      if (wizard.coverLetterMode === 'write' && !wizard.coverLetterText.trim()) {
        e.coverLetterText = 'กรุณากรอกจดหมายแนะนำตัว หรือเลือกวิธีอื่น'
      }
      if (wizard.coverLetterMode === 'upload' && !wizard.coverLetterFile) {
        e.coverLetterFile = 'กรุณาแนบไฟล์ หรือเลือกวิธีอื่น'
      }
    }
    if (currentStep === 'questions') {
      const salary = wizard.expectedSalary.replace(/,/g, '').trim()
      if (salary && (isNaN(Number(salary)) || Number(salary) < 0)) e.expectedSalary = 'กรุณากรอกเป็นตัวเลข'
      for (const q of screeningQuestions) {
        const answer = wizard.screeningAnswers[q.id!]
        const empty = answer === undefined || (typeof answer === 'string' && !answer.trim())
        if (empty) {
          e[`sq_${q.id}`] = 'กรุณาตอบคำถามนี้'   // ต้องตอบทุกข้อ
        } else if (q.type === 'NUMBER' && isNaN(Number(String(answer).replace(/,/g, '').trim()))) {
          e[`sq_${q.id}`] = 'กรุณากรอกเป็นตัวเลข'
        }
      }
    }
    if (currentStep === 'history') {
      wizard.education.forEach((ed, i) => {
        const gpa = ed.gpa.trim()
        if (gpa && (isNaN(Number(gpa)) || Number(gpa) < 0 || Number(gpa) > 4)) e[`gpa_${i}`] = 'เกรดเฉลี่ยต้องอยู่ระหว่าง 0.00–4.00'
      })
    }
    setStepErrors(e)
    return Object.keys(e).length === 0
  }

  // ขั้น "ข้อมูลส่วนตัว" → บันทึก ชื่อ/นามสกุล/เบอร์/จังหวัด กลับเข้าโปรไฟล์ (m_individual)
  // ครั้งหน้าสมัครงานอื่นจะข้ามขั้นนี้ได้ทันที — บันทึกไม่สำเร็จให้หยุดอยู่ขั้นนี้พร้อมแจ้ง error
  const saveProfileStep = async (): Promise<boolean> => {
    if (!profile) return true
    const next = {
      first_name: wizard.firstName.trim(),
      last_name: wizard.lastName.trim(),
      phone: wizard.phone.trim(),
      province: wizard.province,
    }
    const unchanged = next.first_name === profile.first_name && next.last_name === profile.last_name
      && next.phone === (profile.phone || '') && next.province === (profile.province || '')
    if (unchanged) return true
    setIsSubmitting(true)
    try {
      const updated = await updateProfile(next)
      if ('first_name' in updated) setProfile(updated)
      return true
    } catch (err) {
      setStepErrors({ profile: err instanceof Error ? err.message : 'บันทึกข้อมูลส่วนตัวไม่สำเร็จ' })
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const goNext = async () => {
    if (!validateStep()) return
    if (currentStep === 'profile' && !(await saveProfileStep())) return
    if (isLastStep) { await handleSubmit(); return }
    setStepIndex(i => i + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const goBack = () => {
    // /jobs/:id เป็นแค่ redirect กลับมาหน้า apply เอง (ดู JobDetailRedirect ใน App.tsx) — ปุ่มยกเลิกที่ step
    // แรกจึงพากลับไปหน้ารายการงานแทน ไม่ใช่ /jobs/:id ตรงๆ (จะวนกลับมาหน้าเดิม)
    if (isFirstStep) { navigate('/jobs'); return }
    setStepIndex(i => i - 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async () => {
    setSubmitError('')
    setIsSubmitting(true)
    try {
      // ส่งข้อมูลขั้นที่ 2-4 ทั้งหมด — backend ตรวจซ้ำและข้ามแถวประวัติ/การศึกษาที่ว่างทั้งแถวให้เอง
      // ไฟล์ที่เลือกไว้ (Resume/จดหมาย/Transcript/ใบประกาศ) อัปโหลดหลังยื่นใบสมัครสำเร็จ — ดูด้านล่าง
      // ⚠️ (เดิม) ไฟล์ยังไม่ถูกส่ง รอต่อ MinIO แล้วค่อยอัปโหลดจริง
      //    จดหมายแบบ "อัปโหลดไฟล์" จึงส่ง cover_letter เป็น null (เลิกเก็บข้อความ "แนบไฟล์: ชื่อไฟล์" ปลอม)
      const payload: ApplyJobPayload = {
        cover_letter: wizard.coverLetterMode === 'write' ? wizard.coverLetterText : null,
        experience_range: wizard.experience || null,
        expected_salary: wizard.expectedSalary.trim() || null,
        screening_answers: wizard.screeningAnswers,
        work_history: wizard.workHistory.map(w => ({
          position: w.position,
          company: w.company,
          start_date: w.startDate || null,
          end_date: w.current ? null : (w.endDate || null),
          current: w.current,
          description: w.description,
        })),
        education: wizard.education.map(ed => ({
          university: ed.university,
          faculty: ed.faculty,
          major: ed.major,
          gpa: ed.gpa.trim() || null,
          graduated: ed.graduated,
          graduation_date: ed.graduationDate || null,
        })),
        skills: wizard.skills,
      }
      const application = await applyJob(id, payload)

      // แนบไฟล์ — ใบสมัครถูกส่งไปแล้ว ถ้าไฟล์ไหนอัปโหลดไม่สำเร็จจะแจ้งในหน้าสำเร็จ (ใบสมัครไม่ถูกยกเลิก)
      const uploads: { kind: ApplicationDocumentKind; file: File }[] = []
      if (wizard.resumeChoice === 'upload' && wizard.resumeFile) uploads.push({ kind: 'RESUME', file: wizard.resumeFile })
      if (wizard.coverLetterMode === 'upload' && wizard.coverLetterFile) uploads.push({ kind: 'COVER_LETTER', file: wizard.coverLetterFile })
      if (wizard.educationFile) uploads.push({ kind: 'TRANSCRIPT', file: wizard.educationFile })
      for (const file of wizard.certificateFiles) uploads.push({ kind: 'CERTIFICATE', file })
      const failed: string[] = []
      for (const u of uploads) {
        try {
          await uploadApplicationDocument(application.application_id, u.kind, u.file)
        } catch (err) {
          failed.push(`${u.file.name} (${err instanceof Error ? err.message : 'ไม่สำเร็จ'})`)
        }
      }
      setUploadWarning(failed.length ? `ส่งใบสมัครแล้ว แต่แนบไฟล์ไม่สำเร็จ: ${failed.join(', ')} — ติดต่อบริษัทเพื่อส่งเอกสารเพิ่มเติม` : '')
      setScreen('success')
    } catch (err) {
      // server บอกว่ายังไม่ได้ทำแบบประเมินก่อนสมัครงาน → พาไปทำก่อน แล้วกลับมาหน้านี้
      if ((err as { code?: string })?.code === 'PRE_ASSESSMENT_REQUIRED') {
        navigate(preAssessmentPath(`/jobs/${id}/apply`), { replace: true })
        return
      }
      setSubmitError(err instanceof Error ? err.message : 'ยื่นใบสมัครไม่สำเร็จ')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[#FAFAFF] pt-24 pb-16">
      {/* พื้นหลัง logistics แบบจางๆ */}
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />

      <div className="relative mx-auto max-w-[1240px] px-4 md:px-6">

        {/* ── กล่องเดียวรวมทุกอย่าง: header + step indicator + เนื้อหา step + ปุ่มด้านล่าง ── */}
        <div className="border border-[#E7E7F0] bg-white px-6 py-6 sm:px-10">

          <button onClick={() => navigate('/jobs')} className="flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-text">
            <ArrowLeft className="h-4 w-4" /> กลับไปหน้าตำแหน่งงาน
          </button>

          <h1 className="mt-4 font-display text-2xl font-bold leading-tight text-text">สมัครงาน: {job.title}</h1>
          <p className="mt-1 text-sm text-text-muted">{job.company.company_name}</p>

          <div className="mt-6 border-t border-[#E7E7F0] pt-6">
            <StepIndicator steps={steps} currentIndex={stepIndex} />
          </div>

        {stepIndex === 0 && isFirstApplication && (
          <div className="mt-6">
            <DisclosureNotice companyName={job.company.company_name} />
          </div>
        )}

        <div className={`mt-6 grid grid-cols-1 items-stretch gap-8 border-t border-[#E7E7F0] pt-6 ${currentStep === 'profile' ? 'lg:grid-cols-[1fr_360px]' : ''}`}>

          {/* ── LEFT: Step content ── */}
          <div>
            <div>

              {/* STEP: ข้อมูลส่วนตัว */}
              {currentStep === 'profile' && (
                <div>
                  <span className="inline-block rounded-full bg-[#F1F0FF] px-3 py-1 text-xs font-semibold text-accent">
                    Step {stepIndex + 1} / {steps.length}
                  </span>
                  <h2 className="mt-3 text-xl font-bold text-text">ข้อมูลส่วนตัว</h2>
                  <p className="mt-1 text-sm text-text-muted">กรอกข้อมูลพื้นฐานสำหรับการสมัครงาน</p>

                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-text">ชื่อ <span className="text-rose-500">*</span></label>
                      <input value={wizard.firstName} onChange={e => set('firstName', e.target.value)} className={inputCls} placeholder="เช่น สมชาย" />
                      {stepErrors.firstName && <p className="mt-1 text-xs text-rose-500">{stepErrors.firstName}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-text">นามสกุล <span className="text-rose-500">*</span></label>
                      <input value={wizard.lastName} onChange={e => set('lastName', e.target.value)} className={inputCls} placeholder="เช่น ใจดี" />
                      {stepErrors.lastName && <p className="mt-1 text-xs text-rose-500">{stepErrors.lastName}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-text">จังหวัดที่อยู่ปัจจุบัน <span className="text-rose-500">*</span></label>
                      <SearchableSelect
                        value={normalizeProvince(wizard.province)} options={provinces} onChange={v => set('province', v)}
                        className={inputCls} placeholder="เลือกจังหวัด" searchPlaceholder="ค้นหาจังหวัด..."
                      />
                      {stepErrors.province && <p className="mt-1 text-xs text-rose-500">{stepErrors.province}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-text">หมายเลขโทรศัพท์ <span className="text-rose-500">*</span></label>
                      <input value={wizard.phone} onChange={e => set('phone', filterPhone(e.target.value))} inputMode="tel" maxLength={10} className={inputCls} placeholder="เช่น 08X-XXX-XXXX" />
                      {stepErrors.phone && <p className="mt-1 text-xs text-rose-500">{stepErrors.phone}</p>}
                    </div>
                  </div>

                  <div className="mt-6 flex items-start gap-3 bg-[#F5F3FF] p-4">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                      <Info className="h-3.5 w-3.5" />
                    </span>
                    <div>
                      <p className="text-[13px] font-medium text-text">ข้อมูลนี้จะถูกใช้สำหรับการติดต่อเกี่ยวกับการสมัครงานเท่านั้น</p>
                      <p className="mt-0.5 text-xs text-text-muted">หากคุณเคยกรอกข้อมูลส่วนตัวไว้แล้ว ระบบจะข้ามขั้นตอนนี้ในการสมัครงานครั้งถัดไป (แก้ไขภายหลังได้ที่หน้าโปรไฟล์)</p>
                    </div>
                  </div>
                  {stepErrors.profile && (
                    <div className="mt-4 flex items-center gap-2 border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {stepErrors.profile}
                    </div>
                  )}
                </div>
              )}

              {/* STEP: อัปโหลดเอกสาร */}
              {currentStep === 'documents' && (
                <div className="space-y-7">
                  <div>
                    <SectionTitle title="Resume" hint="เลือกได้ว่าจะแนบ Resume หรือไม่" />
                    <div className="flex flex-wrap gap-2">
                      {(['none', 'upload'] as const).map(v => (
                        <button key={v} type="button" onClick={() => set('resumeChoice', v)}
                          className={`border px-4 py-2 text-sm font-medium transition-colors ${
                            wizard.resumeChoice === v ? 'border-accent bg-accent/5 text-accent' : 'border-[#E7E7F0] text-text-muted hover:text-text'
                          }`}>
                          {v === 'none' ? 'ไม่ต้องการอัปโหลด' : 'อัปโหลด Resume'}
                        </button>
                      ))}
                    </div>
                    {wizard.resumeChoice === 'upload' && (
                      <div className="mt-3">
                        {wizard.resumeFile ? (
                          <div className="flex items-center gap-3 border border-[#E7E7F0] px-4 py-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-rose-50 text-[10px] font-bold text-rose-500">FILE</span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-text">{wizard.resumeFile.name}</p>
                            </div>
                            <button type="button" onClick={() => resumeInputRef.current?.click()}
                              className="shrink-0 border border-accent/30 bg-accent/5 px-3 py-1.5 text-xs font-medium text-accent transition-colors hover:bg-accent/10">
                              เปลี่ยนไฟล์
                            </button>
                            <input ref={resumeInputRef} type="file" className="hidden" accept={ACCEPTED.join(',')} onChange={e => handleResumeFile(e.target.files?.[0] ?? null)} />
                          </div>
                        ) : (
                          <div onClick={() => resumeInputRef.current?.click()}
                            className="cursor-pointer border border-dashed border-accent/25 bg-white p-5 text-center transition-colors hover:border-accent/60 hover:bg-accent/5">
                            <Upload className="mx-auto h-5 w-5 mb-2 text-text-muted" />
                            <p className="text-sm font-medium text-text">คลิกเพื่อเลือกไฟล์ Resume</p>
                            <p className="mt-0.5 text-xs text-text-muted">รองรับ .pdf, .doc, .docx, .txt · ขนาดสูงสุด {MAX_MB} MB</p>
                            <input ref={resumeInputRef} type="file" className="hidden" accept={ACCEPTED.join(',')} onChange={e => handleResumeFile(e.target.files?.[0] ?? null)} />
                          </div>
                        )}
                        {stepErrors.resume && <p className="mt-2 text-xs text-rose-500">{stepErrors.resume}</p>}
                      </div>
                    )}
                  </div>

                  <div className="h-px w-full bg-[#E7E7F0]" />

                  <div>
                    <SectionTitle title="จดหมายสมัครงาน" hint="เลือกวิธีแนะนำตัวที่สะดวกที่สุดสำหรับคุณ" />
                    <div className="flex flex-wrap gap-2">
                      {([
                        ['upload', 'อัปโหลดไฟล์แนะนำตัว'],
                        ['write', 'เขียนในเว็บ'],
                        ['none', 'ไม่ต้องการแนบ'],
                      ] as const).map(([v, label]) => (
                        <button key={v} type="button" onClick={() => set('coverLetterMode', v)}
                          className={`border px-4 py-2 text-sm font-medium transition-colors ${
                            wizard.coverLetterMode === v ? 'border-accent bg-accent/5 text-accent' : 'border-[#E7E7F0] text-text-muted hover:text-text'
                          }`}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {wizard.coverLetterMode === 'write' && (
                      <div className="mt-3">
                        <AutoGrowTextarea
                          value={wizard.coverLetterText}
                          minHeight={180}
                          maxLength={COVER_LETTER_MAX}
                          onChange={e => set('coverLetterText', e.target.value)}
                          placeholder={`เรียน ทีม HR ของ ${job.company.company_name}\n\nข้าพเจ้า ${wizard.firstName} ${wizard.lastName} มีความสนใจสมัครในตำแหน่ง ${job.title} เนื่องจาก...`}
                          className={`border bg-white px-4 py-3.5 text-sm text-text placeholder-text-muted/60 focus:outline-none focus:ring-1 transition-colors leading-relaxed ${stepErrors.coverLetterText ? 'border-rose-400' : 'border-[#E7E7F0] focus:border-accent focus:ring-accent/25'}`}
                        />
                        <div className="mt-1.5 flex items-start justify-between gap-3">
                          <div>{stepErrors.coverLetterText && <p className="text-xs text-rose-500">{stepErrors.coverLetterText}</p>}</div>
                          <p className={`shrink-0 text-xs ${wizard.coverLetterText.length >= COVER_LETTER_MAX ? 'text-rose-500' : 'text-text-muted'}`}>
                            {wizard.coverLetterText.length.toLocaleString()}/{COVER_LETTER_MAX.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                    {wizard.coverLetterMode === 'upload' && (
                      <div className="mt-3">
                        <div onClick={() => coverLetterInputRef.current?.click()}
                          className="cursor-pointer border border-dashed border-accent/25 bg-white p-5 text-center transition-colors hover:border-accent/60 hover:bg-accent/5">
                          <Upload className="mx-auto h-5 w-5 mb-2 text-text-muted" />
                          <p className="text-sm font-medium text-text">{wizard.coverLetterFile ? wizard.coverLetterFile.name : 'คลิกเพื่อเลือกไฟล์แนะนำตัว'}</p>
                          <p className="mt-0.5 text-xs text-text-muted">รองรับ .pdf, .doc, .docx, .txt · ขนาดสูงสุด {MAX_MB} MB</p>
                          <input ref={coverLetterInputRef} type="file" className="hidden" accept={ACCEPTED.join(',')} onChange={e => handleCoverLetterFile(e.target.files?.[0] ?? null)} />
                        </div>
                        {stepErrors.coverLetterFile && <p className="mt-1.5 text-xs text-rose-500">{stepErrors.coverLetterFile}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP: ตอบคำถาม */}
              {currentStep === 'questions' && (
                <div className="space-y-7">
                  <div>
                    <SectionTitle title="ประสบการณ์ด้านสายงานโลจิสติกส์" hint="ไม่บังคับ — เลือกช่วงที่ใกล้เคียงที่สุด" />
                    <select value={wizard.experience} onChange={e => set('experience', e.target.value)}
                      className={`${inputCls} sm:max-w-xs`}>
                      <option value="">เลือกประสบการณ์...</option>
                      {experienceOptions.map(code => (
                        <option key={code} value={code}>{EXPERIENCE_RANGE_LABELS[code] ?? code}</option>
                      ))}
                    </select>
                  </div>
                  <div className="h-px w-full bg-[#E7E7F0]" />
                  <div>
                    <SectionTitle title="เงินเดือนที่คาดหวัง" hint="ไม่บังคับ — ระบุเป็นตัวเลขโดยประมาณ (บาท/เดือน)" />
                    <input value={wizard.expectedSalary} onChange={e => set('expectedSalary', e.target.value)}
                      className={`${inputCls} sm:max-w-xs`} placeholder="เช่น 25,000" inputMode="numeric" />
                    {stepErrors.expectedSalary && <p className="mt-1.5 text-xs text-rose-500">{stepErrors.expectedSalary}</p>}
                  </div>
                  {screeningQuestions.length > 0 && (
                    <>
                      <div className="h-px w-full bg-[#E7E7F0]" />
                      <div>
                        <SectionTitle title={`คำถามจาก ${job.company.company_name}`} hint="กรุณาตอบให้ครบทุกข้อ" />
                        <div className="space-y-5">
                          {screeningQuestions.map(q => (
                            <ScreeningQuestionField
                              key={q.id}
                              question={q}
                              value={wizard.screeningAnswers[q.id!]}
                              error={stepErrors[`sq_${q.id}`]}
                              onChange={v => setScreeningAnswer(q.id!, v)}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* STEP: ประวัติ */}
              {currentStep === 'history' && (
                <div className="space-y-7">
                  <div>
                    <SectionTitle title="ประวัติการทำงาน" hint="ไม่บังคับ — เพิ่มได้หลายรายการ" />
                    <div className="space-y-4">
                      {wizard.workHistory.map((w, i) => (
                        <div key={i} className="border border-[#E7E7F0] p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="text-xs font-semibold text-text-muted">ประสบการณ์ #{i + 1}</p>
                            {wizard.workHistory.length > 1 && (
                              <button type="button" onClick={() => removeWorkItem(i)} className="text-text-muted hover:text-rose-500">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input value={w.position} onChange={e => updateWorkItem(i, 'position', e.target.value)} className={inputCls} placeholder="ตำแหน่งงาน" />
                            <input value={w.company} onChange={e => updateWorkItem(i, 'company', e.target.value)} className={inputCls} placeholder="บริษัท" />
                            <div>
                              <label className="mb-1 block text-[11px] text-text-muted">วันที่เริ่ม</label>
                              <input value={w.startDate} onChange={e => updateWorkItem(i, 'startDate', e.target.value)} type="date" className={inputCls} />
                            </div>
                            <div>
                              <label className="mb-1 block text-[11px] text-text-muted">วันที่สิ้นสุด</label>
                              <input value={w.endDate} onChange={e => updateWorkItem(i, 'endDate', e.target.value)} type="date" disabled={w.current}
                                className={`${inputCls} disabled:cursor-not-allowed disabled:bg-[#F5F5F7] disabled:text-text-muted/60`} />
                            </div>
                          </div>

                          <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-text">
                            <input type="checkbox" checked={w.current}
                              onChange={e => {
                                // อัปเดต 2 ฟิลด์ในครั้งเดียว — เรียก updateWorkItem 2 ครั้งติดกันจะทับกันเอง
                                // (ทั้งคู่สร้างจาก wizard.workHistory ชุดเดิม ครั้งหลังเลยล้าง current กลับเป็น false)
                                const next = [...wizard.workHistory]
                                next[i] = { ...next[i], current: e.target.checked, ...(e.target.checked ? { endDate: '' } : {}) }
                                set('workHistory', next)
                              }}
                              className="h-3.5 w-3.5 rounded accent-accent" />
                            ยังอยู่ในตำแหน่งนี้
                          </label>

                          <div className="mt-3">
                            <label className="mb-1 block text-[11px] text-text-muted">รายละเอียด — หน้าที่ความรับผิดชอบ ผลงานที่ผ่านมา ฯลฯ</label>
                            <AutoGrowTextarea
                              value={w.description}
                              minHeight={80}
                              onChange={e => updateWorkItem(i, 'description', e.target.value)}
                              placeholder="เล่าว่าตำแหน่งนี้ได้ทำอะไรมาบ้าง..."
                              className="border border-[#E7E7F0] bg-white px-3.5 py-2.5 text-sm text-text placeholder-text-muted/60 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/25 leading-relaxed"
                            />
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addWorkItem}
                        className="flex items-center gap-1.5 border border-dashed border-accent/30 px-4 py-2 text-xs font-medium text-accent hover:bg-accent/5">
                        <Plus className="h-3.5 w-3.5" /> เพิ่มประสบการณ์ทำงาน
                      </button>
                    </div>
                  </div>

                  <div className="h-px w-full bg-[#E7E7F0]" />

                  <div>
                    <SectionTitle title="ข้อมูลการศึกษา" hint="ไม่บังคับ — เพิ่มได้หลายรายการ" />
                    <div className="space-y-4">
                      {wizard.education.map((ed, i) => (
                        <div key={i} className="border border-[#E7E7F0] p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="text-xs font-semibold text-text-muted">การศึกษา #{i + 1}</p>
                            {wizard.education.length > 1 && (
                              <button type="button" onClick={() => removeEducationItem(i)} className="text-text-muted hover:text-rose-500">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input value={ed.university} onChange={e => updateEducationItem(i, 'university', e.target.value)} className={inputCls} placeholder="มหาวิทยาลัย" />
                            <input value={ed.faculty} onChange={e => updateEducationItem(i, 'faculty', e.target.value)} className={inputCls} placeholder="คณะ" />
                            <input value={ed.major} onChange={e => updateEducationItem(i, 'major', e.target.value)} className={inputCls} placeholder="สาขา" />
                            <div>
                              <input value={ed.gpa} onChange={e => { updateEducationItem(i, 'gpa', e.target.value); setStepErrors(p => ({ ...p, [`gpa_${i}`]: '' })) }}
                                className={inputCls} placeholder="เกรดเฉลี่ย (GPA)" inputMode="decimal" />
                              {stepErrors[`gpa_${i}`] && <p className="mt-1 text-xs text-rose-500">{stepErrors[`gpa_${i}`]}</p>}
                            </div>
                          </div>

                          <div className="mt-3">
                            <p className="mb-1.5 text-[11px] text-text-muted">สถานะการศึกษา</p>
                            <div className="flex flex-wrap gap-2">
                              {([
                                [true, 'จบการศึกษาแล้ว'],
                                [false, 'ยังไม่จบการศึกษา'],
                              ] as const).map(([v, label]) => (
                                <button key={String(v)} type="button" onClick={() => updateEducationItem(i, 'graduated', v)}
                                  className={`border px-4 py-2 text-sm font-medium transition-colors ${
                                    ed.graduated === v ? 'border-accent bg-accent/5 text-accent' : 'border-[#E7E7F0] text-text-muted hover:text-text'
                                  }`}>
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mt-3">
                            <label className="mb-1 block text-[11px] text-text-muted">
                              {ed.graduated ? 'วันที่จบการศึกษา' : 'วันที่คาดว่าจะจบการศึกษา'}
                            </label>
                            <input value={ed.graduationDate} onChange={e => updateEducationItem(i, 'graduationDate', e.target.value)}
                              type="date" className={`${inputCls} sm:max-w-xs`} />
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addEducationItem}
                        className="flex items-center gap-1.5 border border-dashed border-accent/30 px-4 py-2 text-xs font-medium text-accent hover:bg-accent/5">
                        <Plus className="h-3.5 w-3.5" /> เพิ่มข้อมูลการศึกษา
                      </button>
                    </div>

                    <div className="mt-4">
                      <p className="mb-1.5 text-xs text-text-muted">ไม่บังคับ — แนบไฟล์ใบจบการศึกษา/Transcript</p>
                      <div onClick={() => educationInputRef.current?.click()}
                        className="cursor-pointer border border-dashed border-accent/25 bg-white p-5 text-center transition-colors hover:border-accent/60 hover:bg-accent/5">
                        <Upload className="mx-auto h-5 w-5 mb-2 text-text-muted" />
                        <p className="text-sm font-medium text-text">{wizard.educationFile ? wizard.educationFile.name : 'คลิกเพื่ออัปโหลดไฟล์'}</p>
                        <p className="mt-0.5 text-xs text-text-muted">เช่น Transcript, ใบปริญญาบัตร</p>
                        <input ref={educationInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => set('educationFile', e.target.files?.[0] ?? null)} />
                      </div>
                    </div>
                  </div>

                  <div className="h-px w-full bg-[#E7E7F0]" />

                  <div>
                    <SectionTitle title="ใบอนุญาต / ใบประกาศนียบัตร" hint="ไม่บังคับ — แนบได้หลายไฟล์" />
                    <div onClick={() => certificateInputRef.current?.click()}
                      className="cursor-pointer border border-dashed border-accent/25 bg-white p-5 text-center transition-colors hover:border-accent/60 hover:bg-accent/5">
                      <Upload className="mx-auto h-5 w-5 mb-2 text-text-muted" />
                      <p className="text-sm font-medium text-text">คลิกเพื่ออัปโหลดไฟล์ ({wizard.certificateFiles.length} ไฟล์)</p>
                      <input ref={certificateInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                        onChange={e => set('certificateFiles', [...wizard.certificateFiles, ...Array.from(e.target.files || [])])} />
                    </div>
                    {wizard.certificateFiles.length > 0 && (
                      <ul className="mt-2 space-y-1.5">
                        {wizard.certificateFiles.map((f, i) => (
                          <li key={i} className="flex items-center justify-between border border-[#E7E7F0] px-3 py-2 text-xs text-text">
                            <span className="truncate">{f.name}</span>
                            <button type="button" onClick={() => set('certificateFiles', wizard.certificateFiles.filter((_, idx) => idx !== i))} className="text-text-muted hover:text-rose-500">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="h-px w-full bg-[#E7E7F0]" />

                  <div>
                    <SectionTitle title="ทักษะ" hint={`เพิ่มได้สูงสุด ${MAX_SKILLS} ข้อ`} />
                    <div className="flex gap-2">
                      <input value={skillInput} onChange={e => setSkillInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        className={inputCls} placeholder="เช่น Forklift, WMS, Excel" disabled={wizard.skills.length >= MAX_SKILLS} />
                      <button type="button" onClick={addSkill} disabled={wizard.skills.length >= MAX_SKILLS}
                        className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#E7E7F0] text-text-muted transition-colors hover:border-accent/50 hover:text-accent disabled:opacity-40">
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {stepErrors.skills && <p className="mt-1.5 text-xs text-rose-500">{stepErrors.skills}</p>}
                    {wizard.skills.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {wizard.skills.map((s, i) => (
                          <span key={s} className="flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/5 px-3 py-1 text-xs font-medium text-accent">
                            {s}
                            <button type="button" onClick={() => removeSkill(i)}><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP: รีวิวและส่ง */}
              {currentStep === 'review' && (
                <div className="space-y-6">
                  <SectionTitle title="ตรวจสอบข้อมูลก่อนส่งใบสมัคร" />

                  <div className="flex items-center gap-3 bg-[#F5F3FF] p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-sm font-bold text-white shrink-0">
                      <User className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text">{wizard.firstName} {wizard.lastName}</p>
                      <p className="text-xs text-text-muted">{wizard.province || '-'} {wizard.phone && `· ${wizard.phone}`}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="text-xs font-semibold text-text-muted">Resume</p>
                      <p className="mt-1 text-sm text-text">{wizard.resumeChoice === 'upload' && wizard.resumeFile ? wizard.resumeFile.name : 'ไม่แนบ'}</p>
                    </div>
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="text-xs font-semibold text-text-muted">จดหมายสมัครงาน</p>
                      <p className="mt-1 text-sm text-text">
                        {wizard.coverLetterMode === 'write' ? 'เขียนในเว็บ' : wizard.coverLetterMode === 'upload' ? (wizard.coverLetterFile?.name || 'แนบไฟล์') : 'ไม่แนบ'}
                      </p>
                    </div>
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="text-xs font-semibold text-text-muted">ประสบการณ์ด้านสายงานโลจิสติกส์</p>
                      <p className="mt-1 text-sm text-text">{wizard.experience ? (EXPERIENCE_RANGE_LABELS[wizard.experience] ?? wizard.experience) : 'ไม่ระบุ'}</p>
                    </div>
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="text-xs font-semibold text-text-muted">เงินเดือนที่คาดหวัง</p>
                      <p className="mt-1 text-sm text-text">{wizard.expectedSalary || 'ไม่ระบุ'}</p>
                    </div>
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="text-xs font-semibold text-text-muted">ประวัติการทำงาน</p>
                      <p className="mt-1 text-sm text-text">
                        {filledWork.length > 0 ? filledWork.map(w => [w.position, w.company].filter(Boolean).join(' · ')).join(', ') : 'ไม่ระบุ'}
                      </p>
                    </div>
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="text-xs font-semibold text-text-muted">การศึกษา</p>
                      <p className="mt-1 text-sm text-text">
                        {filledEducation.length > 0 ? filledEducation.map(ed => [ed.university, ed.major].filter(Boolean).join(' · ')).join(', ') : 'ไม่ระบุ'}
                      </p>
                    </div>
                    <div className="border border-[#E7E7F0] p-4 sm:col-span-2">
                      <p className="text-xs font-semibold text-text-muted">ทักษะ</p>
                      <p className="mt-1 text-sm text-text">{wizard.skills.length > 0 ? wizard.skills.join(', ') : 'ไม่ระบุ'}</p>
                    </div>
                  </div>

                  {screeningQuestions.length > 0 && (
                    <div className="border border-[#E7E7F0] p-4">
                      <p className="mb-2 text-xs font-semibold text-text-muted">คำตอบคำถามจากบริษัท</p>
                      <ul className="space-y-1.5">
                        {screeningQuestions.map(q => {
                          const a = wizard.screeningAnswers[q.id!]
                          const text = a === true ? 'ใช่' : a === false ? 'ไม่ใช่' : (a && String(a).trim()) || 'ไม่ได้ตอบ'
                          return (
                            <li key={q.id} className="text-sm text-text">
                              <span className="text-text-muted">{q.question}:</span> {text}
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}

                  {submitError && (
                    <div className="flex items-center gap-2 border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {submitError}
                    </div>
                  )}

                  <p className="text-xs text-text-muted">ข้อมูลการสมัครจะถูกส่งให้บริษัทที่คุณสมัครโดยตรง</p>
                </div>
              )}
            </div>

            {/* ── Bottom actions ── */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <button onClick={goBack}
                className="text-sm font-medium text-text-muted transition-colors hover:text-text">
                {isFirstStep ? 'ยกเลิกการสมัคร' : '← ย้อนกลับ'}
              </button>
              <button onClick={goNext} disabled={isSubmitting || (currentStep === 'profile' && !step1Valid)}
                className="flex items-center justify-center gap-2 bg-accent px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:cursor-not-allowed disabled:bg-[#DADCE3] disabled:text-white/80">
                {isSubmitting
                  ? <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> กำลังส่ง...</>
                  : isLastStep
                    ? <><Send className="h-4 w-4" /> ยื่นใบสมัคร</>
                    : <>ถัดไป <ArrowRight className="h-4 w-4" /></>
                }
              </button>
            </div>
          </div>

          {/* ── RIGHT: การ์ดสนับสนุนแบบใหม่ — โชว์เฉพาะ step "ข้อมูลส่วนตัว" เท่านั้น step อื่นไม่มีคอลัมน์ขวาแล้ว (ลบสรุปประกาศงานเดิมทิ้งตามที่ขอ) ── */}
          {currentStep === 'profile' && (
          <div className="h-full">
            <div className="h-full lg:sticky lg:top-[110px]">
                <div className="flex h-full flex-col border border-[#E7E5FF] bg-white p-6">
                  <div className="mx-auto mb-5 w-fit">
                    <ApplicationIllustration />
                  </div>
                  <h3 className="text-center text-base font-bold text-text">เริ่มต้นเส้นทางอาชีพใหม่กับเรา</h3>
                  <p className="mt-1.5 text-center text-xs leading-relaxed text-text-muted">
                    กรอกข้อมูลให้ครบถ้วน เพื่อเพิ่มโอกาสในการได้รับการพิจารณา
                  </p>
                  <div className="mt-5 space-y-2.5">
                    {[
                      'ข้อมูลของคุณจะถูกเก็บเป็นความลับ',
                      'ใช้เวลาเพียงไม่กี่นาที',
                      'กรอกครั้งเดียว ใช้ได้กับการสมัครครั้งถัดไป',
                    ].map(t => (
                      <div key={t} className="flex items-start gap-2 text-[13px] text-text">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {t}
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-1 items-center justify-center bg-[#F5F3FF] px-4 py-3 text-center text-xs italic text-accent">
                    "โอกาสดี ๆ เริ่มต้นได้จากข้อมูลที่ครบถ้วน"
                  </div>
                </div>
            </div>
          </div>
          )}

        </div>
        </div>
      </div>
    </div>
  )
}
